import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { HighPrioritySystemMessage } from '@zg-rentals/satellite-components';
import type { MessageListMessage } from '../..';
import {
  useApplicationRequest_GetDataQuery,
  useInviteToApplyHook_InviteToApplyMutation,
} from '../../../../types/GraphqlTypes';
import { ModalType } from '../../../../types/ModalType';
import StringConstants from '../../../../constants/StringConstants';
import useModal from '../../../../hooks/useModal';
import { APPLICATIONS_UPSELL_MODAL_COOKIE } from '../../../Modals/ApplicationsUpsellModal';
import { logApolloError } from '../../../../utils/errorUtils';
import { logError } from '@zg-rentals/log-error';
import { getCurrentSessionURL } from '../../../../utils/fullStory';
import { appMarkerLogger } from '../../../../bootstrap';

type Props = {
  conversationId: string;
  listingAlias: string;
  messageObject: MessageListMessage;
};

const logger = appMarkerLogger.child({ name: 'ApplicationRequest' });

const ApplicationRequest = ({ conversationId, listingAlias, messageObject }: Props): JSX.Element | null => {
  let ctaText = '';
  const [isSendingApplicationRequest, setIsSendingApplicationRequest] = useState(false);
  const [cookies] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);
  const { setActiveModal } = useModal();

  const { messageLink, message, messageDateMs, messageType, title, uiTreatmentType } = messageObject;

  if (messageLink) {
    ctaText = messageLink.text;
  }

  const { data: conversationData } = useApplicationRequest_GetDataQuery({
    variables: {
      listingAlias: listingAlias || '',
      conversationId: conversationId || '',
    },
    fetchPolicy: 'cache-only',
  });

  const [inviteToApplyMutation] = useInviteToApplyHook_InviteToApplyMutation({
    update(cache) {
      cache.modify({
        id: cache.identify({
          __typename: 'ZRM_Conversation',
          conversationId,
        }),
        fields: {
          applicationAlreadySent() {
            return true;
          },
        },
      });
    },
    onError(error) {
      logApolloError(error, {
        details: 'ApplicationRequest - Error inviting to apply from system message',
      });
    },
  });

  if (!conversationData) {
    return null;
  }

  const { ZRM_getConversationById } = conversationData;
  if (ZRM_getConversationById.applicationAlreadySent) {
    ctaText = StringConstants.SYSTEM_MESSAGE_ACTION.resendApplication;
  }

  const handleSendApplication = async (isRetry = false) => {
    setIsSendingApplicationRequest(true);
    const renterName = ZRM_getConversationById.referenceName || '';
    const renterNameArray = renterName.split(' ') || [];
    const firstName = renterNameArray[0] || '';
    const lastName = renterNameArray.length > 1 ? renterNameArray[renterNameArray.length - 1] : '';
    const variables = {
      listingId: listingAlias || '',
      renterFirstName: firstName,
      renterLastName: lastName,
      toEmail: ZRM_getConversationById.referenceEmail || '',
      resendEmail: isRetry,
    };
    logger.info(variables, 'firing invite to apply from system message');
    try {
      await inviteToApplyMutation({
        variables: {
          listingId: listingAlias || '',
          renterFirstName: firstName,
          renterLastName: lastName,
          toEmail: ZRM_getConversationById.referenceEmail || '',
          resendEmail: false,
        },
      });
      setIsSendingApplicationRequest(false);

      if (!ZRM_getConversationById.isApplicationsAccepted && !cookies[APPLICATIONS_UPSELL_MODAL_COOKIE]) {
        setActiveModal(ModalType.ApplicationsUpsellModal);
      }
    } catch (error) {
      if (!isRetry) {
        return handleSendApplication(true);
      }

      logError({
        error,
        context: {
          fullStorySessionUrl: getCurrentSessionURL(),
          details: 'ApplicationRequest - Error inviting to apply from system message',
          conversationId,
          ...variables,
        },
      });
    }
  };

  return (
    <HighPrioritySystemMessage
      ctaText={isSendingApplicationRequest ? 'Sending application...' : ctaText}
      message={message || ''}
      messageDateMs={messageDateMs}
      messageType={messageType}
      onCtaClick={handleSendApplication}
      title={title || ''}
      uiTreatmentType={uiTreatmentType || ''}
    />
  );
};

export default ApplicationRequest;
